import { CSSProperties, createElement, ReactNode, forwardRef } from 'react';
import {
  useDesignTokens,
  DesignTokens,
  renderInlineCssVars,
  TextColor,
  FontStyle,
} from '../../../theme';

import { postfixTestId, TestIdProps } from '../../../util/testId';
import { Text } from '../../Text';
import cx from 'classnames';
import cn from './SliderGripLabel.css.json';
import './SliderGripLabel.css';

export interface SliderGripLabelProps extends TestIdProps {
  children?: ReactNode;
  className: string | undefined;
  style?: CSSProperties;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  orientation: 'horizontal' | 'vertical';
  /**
   * The position to center the content on. Expected to be from 0 to 1.
   */
  position: number;
  label: string | string[];
  hideLabel: boolean;
  isRange?: boolean;
  disabled?: boolean;
  handleLabelTokens:
    | undefined
    | null
    | {
        theme?: () => Partial<TextColor> | undefined;
        font?: Partial<FontStyle>;
      };
  /**
   * Fired when the grip label is pressed.
   */
  onPress?: () => void;
}

export const SliderGripLabel = forwardRef<HTMLDivElement, SliderGripLabelProps>((props, ref) => {
  const dt = useDesignTokens();

  const tokens = renderInlineCssVars(
    {
      ...getGripLabelThemeTokens(dt, props.handleLabelTokens),
      'spacing-xs': dt.spacing.core.xs,
      'spacing-m': dt.spacing.core.m,
      'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
    },
    'slider-grip-label'
  );

  const {
    [props.orientation === 'vertical' ? 'end' : 'start']: styleSpacerStart,
    [props.orientation === 'vertical' ? 'start' : 'end']: styleSpacerEnd,
    // prettier
  } = flexCenter(props.position);

  const labelClassName = cx(
    cn.label,
    cn.inputPadding[props.orientation],
    props.className,
    props.disabled && cn.disabled,
    (props.hideLabel || props.isRange) && cn.labelHide
  );

  return props.hideLabel ? null : (
    <div
      data-testid={postfixTestId(props.testId, 'container')}
      className={cx(cn.container[props.orientation], props.containerClassName)}
      style={{ ...tokens, ...props.containerStyle }}
      ref={ref}
    >
      <div className={cn.spacerStart} style={styleSpacerStart} />
      {Array.isArray(props.label) ? (
        props.label.map((label, index) => (
          <div
            data-testid={postfixTestId(props.testId, index === 0 ? 'max' : 'min')}
            className={cn.spacerCenter}
            key={index}
          >
            <Text
              className={labelClassName}
              style={props.style}
              onClick={() => props.onPress?.()}
              wrapText={false}
              tabIndex={0}
            >
              {label}
            </Text>
          </div>
        ))
      ) : (
        <div data-testid={props.testId} className={cn.spacerCenter}>
          <Text
            className={labelClassName}
            style={props.style}
            onClick={(event) => {
              // ignore click events that bubble from props.children
              if (event.currentTarget === event.target) {
                props.onPress?.();
              }
            }}
            wrapText={false}
          >
            {props.label}
          </Text>
          {props.children}
        </div>
      )}
      <div className={cn.spacerEnd} style={styleSpacerEnd} />
    </div>
  );
});

function flexCenter(start: number) {
  const end = 1 - start;

  /* Setting the left shrink factor to right and the right shrink factor to left
   * will result in the negative space (space for props.children) being distributed
   * evenly to left and right.
   *
   * flex-shrink below one doesn't really shrink anymore (it grows, sort-of). */
  return {
    start: {
      flexBasis: `${start * 100}%`,
      flexShrink: Math.max(end * 1000, 1),
    },
    end: {
      flexBasis: `${end * 100}%`,
      flexShrink: Math.max(start * 1000, 1),
    },
  };
}

const getGripLabelThemeTokens = (
  dt: DesignTokens,
  overrides: SliderGripLabelProps['handleLabelTokens']
) => {
  let style;
  if (dt.enableNovaTokens) {
    style = {
      fg: dt.c.colorTextLabel,
      'disabled-fg': dt.stateStandardNova.disabled.fg,
    };
  } else {
    switch (dt.themeVariant) {
      case 'high-contrast':
      case 'dark':
        style = {
          fg: dt.c.white,
        };
        break;
      default:
        style = {
          fg: dt.c.n_1,
        };
        break;
    }
  }
  if (overrides) {
    if (overrides.font) {
      style = { ...style, ...overrides.font };
    }
    if (overrides.theme) {
      const theme = overrides.theme();
      if (theme && theme.fg) {
        style = { fg: theme.fg };
      }
    }
  }
  return style;
};
